import { ProductType } from '@/helpers/fetchTariffs/types';
import { EnercityTariffsVersion, Tariff } from '@/models/tariff';
import assetPath from '@uikit/helpers/assetPath';
import { AssetType } from '@uikit/types';
import { IconVariants } from '../Icon/interfaces';
import { createAbsoluteJSXLink } from './helpers';

export const SAVINGS_AMOUNT_THRESHOLD = 50;

export const PRODUCT_DETAILS: Record<
  ProductType,
  {
    label: string;
    consumption: number;
    icon: IconVariants;
  }[]
> = {
  power: [
    {
      consumption: 3940,
      label: 'Familie mit Kindern',
      icon: 'user/family-2',
    },
    {
      consumption: 2275,
      label: 'Paar ohne Kinder',
      icon: 'user/couple',
    },
    {
      consumption: 1590,
      label: 'Single Haushalt',
      icon: 'user/single',
    },
  ],
  gas: [
    {
      consumption: 18_750,
      label: '150 m² Wohnfläche',
      icon: 'user/surface-100',
    },
    {
      consumption: 12_500,
      label: '100 m² Wohnfläche',
      icon: 'user/surface-66',
    },
    {
      consumption: 6250,
      label: '50 m² Wohnfläche',
      icon: 'user/surface-25',
    },
  ],
};

export const DEFAULT_IMAGES = {
  CHECKLIST: {
    path: `${assetPath}/assets/regio/image_checklist.jpeg`,
    name: 'image_checklist',
  },
  TEASER_MORE_INFO: {
    path: `${assetPath}/assets/regio/image_teaser_1.jpeg`,
    name: 'image_teaser_1',
  },
  TEASER_CONTACT: {
    path: `${assetPath}/assets/regio/image_teaser_2.jpeg`,
    name: 'image_teaser_2',
  },
  SECTION_BACKGROUND: `${assetPath}/assets/regio/section_background.svg`,
};

export const FALLBACK_TARIFFS: Tariff[] = [
  {
    id: 'ELECTRICITY_SUBSCRIPTION_BASE_GVV',
    name: 'natürlich versorgt',
    productType: 'power',
    prices: {
      yearly: '1.321,52',
      monthly: '110,00',
      unit: '31,54',
      base: '6,57',
    },
    taxRate: 19,
    platform: EnercityTariffsVersion.New,
    newPlatformInfo: {
      ppmBasic: {
        value: 6.570833333333334,
        unit: 'EUR',
      },
      ppKWh: {
        value: 31.54,
        unit: 'ct',
      },
      ppmTotal: {
        value: 110,
        unit: 'EUR',
      },
      pyTotal: {
        value: 1321.52,
        unit: 'EUR',
      },
      discountBonus: {
        value: null,
        unit: 'months',
      },
      loyaltyBonus: {
        value: null,
        unit: 'months',
      },
      discountBonusPeriod: {
        value: null,
        unit: 'months',
      },
      loyaltyBonusDelay: {
        value: null,
        unit: 'months',
      },
      discountOfferRate: {
        value: null,
        unit: '%',
      },
    },
    prio: '1',
    attachments: [
      {
        name: 'Versorgungsbedingungen',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-versorgt-strom/enercity-Versorgungsbedingungen-f-r-Strom.pdf',
        type: AssetType.Pdf,
      },
      {
        name: 'Allgemeine Vertragsinformation',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-versorgt-strom/Allgemeine-Vertragsinformationen-enercity-Strom-nat-rlich-versorgt.pdf',
        type: AssetType.Pdf,
      },
      {
        name: 'Preisinformation',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-versorgt-strom/Preisinformation-enercity-Strom-nat-rlich-versorgt_01-04-2021.pdf',
        type: AssetType.Pdf,
      },
      {
        name: 'Stromkennzeichnung',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-versorgt-strom/StrommixInfoPDF.pdf',
        type: AssetType.Pdf,
      },
    ],
    information: {
      subtitle: 'enercity Strom',
      shortDescription: [
        'Grundversorgungstarif',
        '14-tägige Kündigungsfrist',
        '100 % Ökostrom',
      ],
      moreInfo:
        '<p><strong>100 Prozent &Ouml;kostrom aus Wasserkraft</strong><br />\nIhr Strom wird zu 100 % mit Wasserkraft erzeugt.</p>\n\n<p><strong>Volle Kostenkontrolle und Flexibilit&auml;t</strong><br />\nBehalten Sie durch Eingabe Ihrer Z&auml;hlerst&auml;nde Verbrauch und Kosten im Blick. Falls n&ouml;tig k&ouml;nnen Sie Ihren Abschlag jederzeit anpassen.</p>\n\n<p><strong>14-t&auml;gige K&uuml;ndigungsfrist</strong><br />\nSie k&ouml;nnen Ihren Vertrag 14 Tage im Voraus k&uuml;ndigen. In der Ersatzversorgung k&ouml;nnen Sie Ihren Vertrag jederzeit k&uuml;ndigen.</p>\n\n<p><strong>Zusammensetzung Ihres Strompreises</strong><br />\nDer Gesamtpreis setzt sich aus dem Arbeitspreis und dem Grundpreis zusammen. S&auml;mtliche Preise sind auf zwei Nachkommastellen gerundet.</p>\n\n<p><strong>Preisanpassung</strong><br />\nFalls sich Ihr Strompreis &auml;ndern sollte, informieren wir Sie mind. sechs Wochen im Voraus.</p>\n\n<p><strong>Grundversorgungstarif</strong><br />\nDer Grundversorger ist das Energieversorgungsunternehmen, das die meisten Haushaltskunden vor Ort mit Strom und/oder Gas beliefert. Der Grundversorger stellt sicher, dass jeder mit Energie beliefert wird.</p>\n\n<p>&nbsp;</p>\n',
    },
    icon: {
      path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-versorgt-strom/Stromicon_natuerlich_versorgt.svg',
      name: 'stromicon-natuerlich-versorgt',
      type: AssetType.Svg,
      calculatedAspectRatio: 100,
      dimensions: {
        width: 72,
        height: 72,
      },
    },
  },
  {
    id: 'ELECTRICITY_SUBSCRIPTION_BASE_18',
    name: 'natürlich garantiert XL',
    productType: 'power',
    prices: {
      yearly: '1.906,87',
      monthly: '159,00',
      unit: '45,48',
      base: '9,58',
    },
    bonus: {
      bonusId: '3404f98c-80f1-48b4-9282-e77b5cf096ba',
      product: 'ELECTRICITY_SUBSCRIPTION_BASE_18',
      amount: '20,00',
      description: 'Sofortbonus',
      payoutDate: '30',
    },
    taxRate: 19,
    platform: EnercityTariffsVersion.New,
    newPlatformInfo: {
      ppmBasic: {
        value: 9.583333333333334,
        unit: 'EUR',
      },
      ppKWh: {
        value: 45.48,
        unit: 'ct',
      },
      ppmTotal: {
        value: 159,
        unit: 'EUR',
      },
      pyTotal: {
        value: 1906.87,
        unit: 'EUR',
      },
      discountBonus: {
        value: null,
        unit: 'months',
      },
      loyaltyBonus: {
        value: null,
        unit: 'months',
      },
      discountBonusPeriod: {
        value: null,
        unit: 'months',
      },
      loyaltyBonusDelay: {
        value: null,
        unit: 'months',
      },
      discountOfferRate: {
        value: null,
        unit: '%',
      },
    },
    prio: '60',
    attachments: [
      {
        name: 'Versorgungsbedingungen',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-xl-strom/enercity-Versorgungsbedingungen-f-r-Strom.pdf',
        type: AssetType.Pdf,
      },
      {
        name: 'Vertragsbedingungen',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-xl-strom/Vertragsbedingungen-enercity-Strom-nat-rlich-garantiert-XL.pdf',
        type: AssetType.Pdf,
      },
      {
        name: 'Allgemeine Vertragsinformation',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-xl-strom/Allgemeine-Vertragsinformationen-enercity-Strom-nat-rlich-garantiert--XL.pdf',
        type: AssetType.Pdf,
      },
      {
        name: 'Stromkennzeichnung',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-xl-strom/Stromkennzeichnung-enercity-Strom-nat-rlich-garantiert-XL.pdf',
        type: AssetType.Pdf,
      },
    ],
    information: {
      subtitle: 'enercity Strom',
      shortDescription: [
        '1,5 Jahre Preisgarantie',
        '1,5 Jahre Vertragslaufzeit',
        '100 % Ökostrom',
        'Exklusiv für Neukunden',
      ],
      moreInfo:
        '<p><strong>100 % &Ouml;kostrom aus Wasserkraft</strong><br />\nIhr Strom wird zu 100 % mit Wasserkraft erzeugt.</p>\n\n<p><strong>Vertragslaufzeit</strong><br />\nIhr Vertrag l&auml;uft 1,5 Jahre ab dem Start der Belieferung. Nach Ablauf der Vertragslaufzeit k&ouml;nnen Sie Ihren Vertrag jeden Tag einen Monat im Voraus k&uuml;ndigen.</p>\n\n<p><strong>Sofortbonus</strong><br />\nSie erhalten ab einem Verbrauch von 500 kWh einen einmaligen Sofortbonus. Der Bonus wird 30 Tage nach Belieferungsstart ausgezahlt. Der Bonusbetrag ist im angezeigten Monatspreis bereits enthalten.</p>\n\n<p><strong>Volle Kostenkontrolle und Flexibilit&auml;t</strong><br />\nBehalten Sie durch Eingabe Ihrer Z&auml;hlerst&auml;nde Verbrauch und Kosten im Blick. Falls n&ouml;tig k&ouml;nnen Sie Ihren Abschlag jederzeit anpassen.</p>\n\n<p><strong>Zusammensetzung Ihres Strompreises</strong><br />\nDer Gesamtpreis setzt sich aus dem Arbeitspreis und dem Grundpreis zusammen. S&auml;mtliche Preise sind auf zwei Nachkommastellen gerundet.</p>\n\n<p><strong>Preisgarantie und Preisanpassung</strong><br />\nSie erhalten von uns eine Preisgarantie &uuml;ber 18 Monate (1,5 Jahre) ab dem Start der Belieferung (ausgenommen Umsatzsteuer). Falls sich Ihr Strompreis danach &auml;ndern sollte, informieren wir Sie mind. 1 Monat im Voraus.</p>\n',
    },
    icon: {
      path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-xl-strom/Stromicon-natuerlich-garantiert-XL.svg',
      name: 'stromicon-natuerlich-garantiert-xl',
      type: AssetType.Svg,
      calculatedAspectRatio: 100,
      dimensions: {
        width: 72,
        height: 72,
      },
    },
  },
  {
    id: 'ELECTRICITY_SUBSCRIPTION_BASE_12',
    name: 'natürlich garantiert',
    productType: 'power',
    prices: {
      yearly: '1.859,99',
      monthly: '155,00',
      unit: '44,29',
      base: '9,58',
    },
    taxRate: 19,
    platform: EnercityTariffsVersion.New,
    newPlatformInfo: {
      ppmBasic: {
        value: 9.583333333333334,
        unit: 'EUR',
      },
      ppKWh: {
        value: 44.29,
        unit: 'ct',
      },
      ppmTotal: {
        value: 155,
        unit: 'EUR',
      },
      pyTotal: {
        value: 1859.99,
        unit: 'EUR',
      },
      discountBonus: {
        value: null,
        unit: 'months',
      },
      loyaltyBonus: {
        value: null,
        unit: 'months',
      },
      discountBonusPeriod: {
        value: null,
        unit: 'months',
      },
      loyaltyBonusDelay: {
        value: null,
        unit: 'months',
      },
      discountOfferRate: {
        value: null,
        unit: '%',
      },
    },
    prio: '99',
    attachments: [
      {
        name: 'Versorgungsbedingungen',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-strom/generalSupplyPower.pdf',
        type: AssetType.Pdf,
      },
      {
        name: 'Vertragsbedingungen',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-strom/Vertragsbedingungen-enercity-Strom-nat-rlich-garantiert_28-06-2021.pdf',
        type: AssetType.Pdf,
      },
      {
        name: 'Allgemeine Vertragsinformation',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-strom/Allgemeine-Vertragsinformationen-enercity-Strom-nat-rlich-garantiert.pdf',
        type: AssetType.Pdf,
      },
      {
        name: 'Stromkennzeichnung',
        path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-strom/Stromkennzeichnung_enercity-Strom-nat-rlich-garantiert.pdf',
        type: AssetType.Pdf,
      },
    ],
    information: {
      subtitle: 'enercity Strom',
      badge: 'Unsere Empfehlung',
      shortDescription: [
        '1 Jahr Preisgarantie',
        '1 Jahr Vertragslaufzeit',
        '100 % Ökostrom',
      ],
      moreInfo:
        '<p><strong>100 % &Ouml;kostrom aus Wasserkraft</strong><br />\nIhr Strom wird zu 100 % mit Wasserkraft erzeugt.</p>\n\n<p><strong>Vertragslaufzeit</strong><br />\nIhr Vertrag l&auml;uft 1 Jahr ab dem Start der Belieferung. Nach Ablauf der Vertragslaufzeit k&ouml;nnen Sie Ihren Vertrag jeden Tag einen Monat im Voraus k&uuml;ndigen.</p>\n\n<p><strong>Abnahmemenge</strong><br />\nAb 1 kWh pro Jahr bis maximal 30.000 kWh pro Jahr.</p>\n\n<p><strong>Volle Kostenkontrolle und Flexibilit&auml;t</strong><br />\nBehalten Sie durch Eingabe Ihrer Z&auml;hlerst&auml;nde Verbrauch und Kosten im Blick. Falls n&ouml;tig k&ouml;nnen Sie Ihren Abschlag jederzeit anpassen.</p>\n\n<p><strong>Zusammensetzung Ihres Strompreises</strong><br />\nDer Gesamtpreis setzt sich aus dem Arbeitspreis und dem Grundpreis zusammen. S&auml;mtliche Preise sind auf zwei Nachkommastellen gerundet.</p>\n\n<p><strong>Preisgarantie und Preisanpassung</strong><br />\nSie erhalten von uns eine Preisgarantie &uuml;ber 1 Jahr ab dem Start der Belieferung (ausgenommen Umsatzsteuer). Falls sich Ihr Strompreis danach &auml;ndern sollte, informieren wir Sie mind. 1 Monat im Voraus.</p>\n',
    },
    icon: {
      path: '/assets/cms/enercity-de/Privatkunden/Produkte/strom-gas/tarife/ed4/natuerlich-garantiert-strom/Stromicon-natuerlich-garantiert.svg',
      name: 'stromicon-natuerlich-garantiert',
      type: AssetType.Svg,
      calculatedAspectRatio: 100,
      dimensions: {
        width: 72,
        height: 72,
      },
    },
  },
];

export const relatedLinks = {
  emob: createAbsoluteJSXLink({
    name: 'Ladesäulen für Elektroautos',
    path: '/privatkunden/produkte/elektromobilitaet/wallbox',
  }),
  energySaving: createAbsoluteJSXLink({
    name: 'top Energiespartipps',
    path: '/privatkunden/service/energiesparen',
  }),
  gas: createAbsoluteJSXLink({
    name: 'Gas',
    path: '/privatkunden/produkte/gas',
  }),
  heat: createAbsoluteJSXLink({
    name: 'Wärmelösungen',
    path: '/privatkunden/produkte/waerme',
  }),
  power: createAbsoluteJSXLink({
    name: 'Strom',
    path: '/privatkunden/produkte/strom',
  }),
  powerComparison: createAbsoluteJSXLink({
    name: 'Vergleich von Stromtarifen',
    path: '/privatkunden/produkte/strom/vergleich',
  }),
  solar: createAbsoluteJSXLink({
    name: 'Solaranlagen (mit Speicher)',
    path: '/privatkunden/produkte/solar',
  }),
  ecoPower: createAbsoluteJSXLink({
    name: 'Ökostrom',
    path: '/privatkunden/produkte/strom/oekostrom',
  }),
};
